import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  CircleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DirectionalLight,
  DodecahedronGeometry,
  FogExp2,
  Group,
  HemisphereLight,
  InstancedMesh,
  Line,
  LineBasicMaterial,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  MeshStandardMaterial,
  Object3D,
  OrthographicCamera,
  PlaneGeometry,
  Raycaster,
  RepeatWrapping,
  RingGeometry,
  Scene,
  SphereGeometry,
  SRGBColorSpace,
  TorusGeometry,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { useEffect, useRef, useState } from "react";

import { CAMERA_YAW, cameraPanAxes, placeCamera } from "./camera-rig";
import { landQuadCorners } from "./terrain-winding";
import {
  FRESHWATER_MOISTURE,
  RIDGE_ELEVATION,
  WORLD_HEIGHT,
  WORLD_WIDTH,
  type BuildingKind,
  type BuildingRecord,
  type CreatureAction,
  type CreatureRecord,
  type ResourceFamily,
  type TileRecord,
  type WorldState,
} from "../simulation/world";

interface WorldCanvasProperties {
  onSelectBuilding: (buildingId: string) => void;
  onSelectCreature: (creatureId: string) => void;
  selectedBuildingId: string | null;
  selectedCreatureId: string | null;
  world: WorldState | null;
}

interface CreatureVisual {
  actionRing: Mesh;
  group: Group;
  pickTargets: Object3D[];
  selectionRing: Mesh;
  targetPosition: Vector3;
}

interface BuildingVisual {
  group: Group;
  pickTargets: Object3D[];
  selectionRing: Mesh;
}

interface KitTemplates {
  hearth: Group | null;
  hutEast: Group | null;
  hutWest: Group | null;
  leafy: Group | null;
  pine: Group | null;
  storage: Group | null;
  work: Group | null;
}

interface WorldRendererState {
  buildingPickTargets: Object3D[];
  buildingVisuals: Map<string, BuildingVisual>;
  camera: OrthographicCamera;
  creaturePickTargets: Object3D[];
  creatureVisuals: Map<string, CreatureVisual>;
  destinationRing: Mesh;
  decorationGroup: Group;
  humanoidTemplates: { east: Group; west: Group } | null;
  kits: KitTemplates;
  landMesh: Mesh | null;
  lodFarGroup: Group;
  onSelectBuilding: (buildingId: string) => void;
  onSelectCreature: (creatureId: string) => void;
  pan: Vector2;
  pointerTravel: number;
  precipitationMesh: InstancedMesh | null;
  renderer: WebGLRenderer;
  resetView: () => void;
  routeLine: Line;
  scene: Scene;
  setZoom: (zoom: number) => void;
  tileById: Map<number, TileRecord>;
  world: WorldState;
}

interface DisposableResource {
  dispose: () => void;
}

const minimumZoom = 0.7;
const maximumZoom = 12;
const humanoidWorldScale = 0.34;
const frameIntervalMs = 1000 / 24;
const landTileHeight = 0.12;
const waterSurface = 0.02;
const mapFitPadding = 1.08;
const worldFitCorners = [
  new Vector3(-WORLD_WIDTH / 2, 0, -WORLD_HEIGHT / 2),
  new Vector3(WORLD_WIDTH / 2, 0, -WORLD_HEIGHT / 2),
  new Vector3(-WORLD_WIDTH / 2, 0, WORLD_HEIGHT / 2),
  new Vector3(WORLD_WIDTH / 2, 0, WORLD_HEIGHT / 2),
  new Vector3(-WORLD_WIDTH / 2, 3.2, -WORLD_HEIGHT / 2),
  new Vector3(WORLD_WIDTH / 2, 3.2, -WORLD_HEIGHT / 2),
  new Vector3(-WORLD_WIDTH / 2, 3.2, WORLD_HEIGHT / 2),
  new Vector3(WORLD_WIDTH / 2, 3.2, WORLD_HEIGHT / 2),
];

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

function tileWorldX(tile: TileRecord) {
  return tile.x - WORLD_WIDTH / 2 + 0.5;
}

function tileWorldZ(tile: TileRecord) {
  return tile.y - WORLD_HEIGHT / 2 + 0.5;
}

function isFreshwater(tile: TileRecord) {
  return tile.walkable && tile.moisture >= FRESHWATER_MOISTURE;
}

function isRidge(tile: TileRecord) {
  return tile.walkable && tile.elevation >= RIDGE_ELEVATION;
}

function tileHeight(tile: TileRecord) {
  if (tile.terrain === "deep-water") return 0.04;
  if (isFreshwater(tile)) return waterSurface + 0.03;
  const ridgeLift = isRidge(tile) ? 0.08 : 0;
  return landTileHeight + tile.elevation * 0.1 + ridgeLift;
}

function tileBaseY(tile: TileRecord) {
  return tile.terrain === "deep-water" ? -0.04 : 0;
}

function tileSurfaceY(tile: TileRecord) {
  return tileBaseY(tile) + tileHeight(tile);
}

function collectShores(world: WorldState) {
  const beach = new Set<number>();
  const shallows = new Set<number>();
  const directions: [number, number][] = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ];

  for (const tile of world.tiles) {
    for (const [dx, dy] of directions) {
      const neighbor = world.tiles[(tile.y + dy) * WORLD_WIDTH + (tile.x + dx)];
      if (!neighbor) continue;
      if (tile.walkable && !neighbor.walkable) beach.add(tile.id);
      if (!tile.walkable && neighbor.walkable) shallows.add(tile.id);
    }
  }

  return { beach, shallows };
}

function terrainColor(
  tile: TileRecord,
  beach: Set<number>,
  shallows: Set<number>,
) {
  if (tile.terrain === "deep-water") {
    return shallows.has(tile.id) ? 0x7adcec : 0x42c4dc;
  }
  if (isFreshwater(tile)) return 0x5ec8dc;
  if (beach.has(tile.id)) return 0xf2d39a;
  if (tile.terrain === "forest") {
    return tile.continentId === "west" ? 0x74c43c : 0x68c040;
  }
  return tile.continentId === "west" ? 0x8adc44 : 0x7ad63c;
}

function variedColor(baseColor: number, id: number, strength = 0.06) {
  const variation = ((id * 37) % 19) / 18 - 0.5;
  return new Color(baseColor).offsetHSL(
    0,
    variation * 0.04,
    variation * strength,
  );
}

function lambert(
  color: number,
  extras: { emissive?: number; emissiveIntensity?: number } = {},
) {
  return new MeshLambertMaterial({
    color,
    flatShading: true,
    ...extras,
  });
}

function configureMesh(mesh: Mesh) {
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  return mesh;
}

function tileAt(world: WorldState, x: number, y: number) {
  if (x < 0 || y < 0 || x >= WORLD_WIDTH || y >= WORLD_HEIGHT) return undefined;
  return world.tiles[y * WORLD_WIDTH + x];
}

function cornerHeight(world: WorldState, x: number, y: number) {
  let total = 0;
  let count = 0;
  const samples: [number, number][] = [
    [x - 1, y - 1],
    [x, y - 1],
    [x - 1, y],
    [x, y],
  ];
  for (const [tileX, tileY] of samples) {
    const tile = tileAt(world, tileX, tileY);
    if (tile?.walkable) {
      total += tileSurfaceY(tile);
      count += 1;
    }
  }
  return count === 0 ? waterSurface : total / count;
}

function cornerWorld(x: number, y: number, height: number) {
  return new Vector3(x - WORLD_WIDTH / 2, height, y - WORLD_HEIGHT / 2);
}

function pushColoredVertex(
  positions: number[],
  colors: number[],
  point: Vector3,
  color: Color,
) {
  positions.push(point.x, point.y, point.z);
  colors.push(color.r, color.g, color.b);
}

function pushQuad(
  positions: number[],
  colors: number[],
  corners: [Vector3, Vector3, Vector3, Vector3],
  tints: [Color, Color, Color, Color],
) {
  const [first, second, third, fourth] = corners;
  const [firstTint, secondTint, thirdTint, fourthTint] = tints;
  pushColoredVertex(positions, colors, first, firstTint);
  pushColoredVertex(positions, colors, second, secondTint);
  pushColoredVertex(positions, colors, third, thirdTint);
  pushColoredVertex(positions, colors, first, firstTint);
  pushColoredVertex(positions, colors, third, thirdTint);
  pushColoredVertex(positions, colors, fourth, fourthTint);
}

function cornerTint(
  world: WorldState,
  x: number,
  y: number,
  beach: Set<number>,
  shallows: Set<number>,
) {
  const mix = new Color(0, 0, 0);
  let count = 0;
  const samples: [number, number][] = [
    [x - 1, y - 1],
    [x, y - 1],
    [x - 1, y],
    [x, y],
  ];
  for (const [tileX, tileY] of samples) {
    const tile = tileAt(world, tileX, tileY);
    if (!tile?.walkable) continue;
    mix.add(variedColor(terrainColor(tile, beach, shallows), tile.id, 0.02));
    count += 1;
  }
  if (count === 0) return new Color(0x42c4dc);
  mix.multiplyScalar(1 / count);
  return mix;
}

function createGrassTexture() {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (!context) {
    return null;
  }
  context.fillStyle = "#86d63c";
  context.fillRect(0, 0, size, size);
  for (let index = 0; index < 2200; index += 1) {
    const x = (index * 53) % size;
    const y = (index * 97) % size;
    const tall = 3 + (index % 5);
    context.strokeStyle =
      index % 3 === 0 ? "rgba(58, 140, 28, 0.28)" : "rgba(170, 230, 80, 0.22)";
    context.beginPath();
    context.moveTo(x, y + tall);
    context.lineTo(x + ((index % 3) - 1), y);
    context.stroke();
  }
  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(WORLD_WIDTH / 6, WORLD_HEIGHT / 6);
  texture.needsUpdate = true;
  return texture;
}

function createColoredMesh(
  positions: number[],
  colors: number[],
  material: MeshBasicMaterial,
) {
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new BufferAttribute(new Float32Array(positions), 3),
  );
  geometry.setAttribute(
    "color",
    new BufferAttribute(new Float32Array(colors), 3),
  );
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  material.vertexColors = true;
  const mesh = new Mesh(geometry, material);
  mesh.name = "terrain-surface";
  mesh.receiveShadow = false;
  mesh.castShadow = false;
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  return mesh;
}

function createTerrainMeshes(world: WorldState, parent: Object3D) {
  const { beach, shallows } = collectShores(world);
  const landPositions: number[] = [];
  const landColors: number[] = [];
  const shorePositions: number[] = [];
  const shoreColors: number[] = [];
  const sand = new Color(0xecd39a);
  const directions: [number, number][] = [
    [0, -1],
    [1, 0],
    [0, 1],
    [-1, 0],
  ];

  for (const tile of world.tiles) {
    if (!tile.walkable) continue;
    const southWest = cornerWorld(
      tile.x,
      tile.y,
      cornerHeight(world, tile.x, tile.y),
    );
    const southEast = cornerWorld(
      tile.x + 1,
      tile.y,
      cornerHeight(world, tile.x + 1, tile.y),
    );
    const northEast = cornerWorld(
      tile.x + 1,
      tile.y + 1,
      cornerHeight(world, tile.x + 1, tile.y + 1),
    );
    const northWest = cornerWorld(
      tile.x,
      tile.y + 1,
      cornerHeight(world, tile.x, tile.y + 1),
    );
    const southWestTint = cornerTint(world, tile.x, tile.y, beach, shallows);
    const southEastTint = cornerTint(
      world,
      tile.x + 1,
      tile.y,
      beach,
      shallows,
    );
    const northEastTint = cornerTint(
      world,
      tile.x + 1,
      tile.y + 1,
      beach,
      shallows,
    );
    const northWestTint = cornerTint(
      world,
      tile.x,
      tile.y + 1,
      beach,
      shallows,
    );
    pushQuad(
      landPositions,
      landColors,
      landQuadCorners(southWest, southEast, northEast, northWest),
      landQuadCorners(
        southWestTint,
        southEastTint,
        northEastTint,
        northWestTint,
      ),
    );

    for (const [dx, dy] of directions) {
      const neighbor = tileAt(world, tile.x + dx, tile.y + dy);
      if (neighbor?.walkable) continue;
      const topA =
        dx === 1
          ? southEast
          : dx === -1
            ? southWest
            : dy === 1
              ? northWest
              : southWest;
      const topB =
        dx === 1
          ? northEast
          : dx === -1
            ? northWest
            : dy === 1
              ? northEast
              : southEast;
      const bottomA = topA.clone();
      const bottomB = topB.clone();
      bottomA.y = waterSurface;
      bottomB.y = waterSurface;
      const shoreTint: [Color, Color, Color, Color] = [sand, sand, sand, sand];
      pushQuad(
        shorePositions,
        shoreColors,
        dx === 1 || dy === -1
          ? [topA, topB, bottomB, bottomA]
          : [topB, topA, bottomA, bottomB],
        shoreTint,
      );
    }
  }

  const land = createColoredMesh(
    landPositions,
    landColors,
    new MeshBasicMaterial({
      map: createGrassTexture(),
      vertexColors: true,
    }),
  );
  land.name = "terrain-land";
  land.renderOrder = 1;
  parent.add(land);
  land.updateMatrix();

  if (shorePositions.length > 0) {
    const shore = createColoredMesh(
      shorePositions,
      shoreColors,
      new MeshBasicMaterial({ vertexColors: true }),
    );
    shore.name = "terrain-shore";
    parent.add(shore);
    shore.updateMatrix();
  }

  const water = new Mesh(
    new PlaneGeometry(WORLD_WIDTH + 40, WORLD_HEIGHT + 40),
    new MeshBasicMaterial({ color: 0x42c4dc }),
  );
  water.name = "terrain-water";
  water.renderOrder = 0;
  water.rotation.x = -Math.PI / 2;
  water.position.y = waterSurface;
  water.castShadow = false;
  water.receiveShadow = false;
  water.frustumCulled = false;
  water.matrixAutoUpdate = false;
  parent.add(water);
  water.updateMatrix();
  parent.updateMatrixWorld(true);
  return { beach, land, shallows };
}

function createTerrainDecorations(
  world: WorldState,
  scene: Scene,
  beach: Set<number>,
  lodFarGroup: Group,
  treeGroup: Group,
) {
  const pineTiles = world.tiles.filter(
    (tile) =>
      tile.walkable &&
      !beach.has(tile.id) &&
      !isFreshwater(tile) &&
      tile.terrain === "forest" &&
      (tile.continentId === "west" || tile.id % 7 === 0) &&
      tile.id % 4 === 0,
  );
  const leafyTiles = world.tiles.filter(
    (tile) =>
      tile.walkable &&
      !beach.has(tile.id) &&
      !isFreshwater(tile) &&
      (tile.terrain === "forest"
        ? tile.continentId === "east" && tile.id % 7 !== 0 && tile.id % 4 === 0
        : tile.terrain === "grassland" && tile.id % 22 === 0),
  );
  const roughTiles = world.tiles.filter(
    (tile) =>
      tile.terrain === "rough-ground" && isRidge(tile) && tile.id % 4 === 0,
  );
  const dummy = new Object3D();
  const bark = new MeshLambertMaterial({
    color: 0x6a4a2e,
    flatShading: true,
  });
  const leafMaterial = new MeshLambertMaterial({
    color: 0xffffff,
    flatShading: true,
  });

  if (pineTiles.length > 0) {
    const pineTrunks = new InstancedMesh(
      new CylinderGeometry(0.05, 0.09, 0.55, 5),
      bark,
      pineTiles.length,
    );
    const pineCanopies = new InstancedMesh(
      new ConeGeometry(0.52, 1.35, 7),
      leafMaterial,
      pineTiles.length,
    );
    pineTiles.forEach((tile, index) => {
      const surface = tileSurfaceY(tile);
      const scale = 1.12 + ((tile.id * 13) % 9) / 22;
      dummy.position.set(
        tileWorldX(tile) + ((tile.id * 11) % 7) / 22 - 0.12,
        surface + 0.26 * scale,
        tileWorldZ(tile) + ((tile.id * 17) % 7) / 22 - 0.12,
      );
      dummy.scale.set(scale, scale, scale);
      dummy.updateMatrix();
      pineTrunks.setMatrixAt(index, dummy.matrix);
      dummy.position.y = surface + 0.92 * scale;
      dummy.updateMatrix();
      pineCanopies.setMatrixAt(index, dummy.matrix);
      pineCanopies.setColorAt(index, variedColor(0x2f8a3a, tile.id, 0.12));
    });
    pineTrunks.instanceMatrix.needsUpdate = true;
    pineCanopies.instanceMatrix.needsUpdate = true;
    if (pineCanopies.instanceColor)
      pineCanopies.instanceColor.needsUpdate = true;
    pineTrunks.castShadow = false;
    pineCanopies.castShadow = false;
    treeGroup.add(pineTrunks, pineCanopies);
  }

  if (leafyTiles.length > 0) {
    const leafyTrunks = new InstancedMesh(
      new CylinderGeometry(0.06, 0.1, 0.42, 5),
      bark,
      leafyTiles.length,
    );
    const leafyCanopies = new InstancedMesh(
      new SphereGeometry(0.62, 8, 6),
      leafMaterial,
      leafyTiles.length,
    );
    leafyTiles.forEach((tile, index) => {
      const surface = tileSurfaceY(tile);
      const scale = 1.08 + ((tile.id * 9) % 8) / 18;
      dummy.position.set(
        tileWorldX(tile) + ((tile.id * 7) % 6) / 20 - 0.12,
        surface + 0.2 * scale,
        tileWorldZ(tile) + ((tile.id * 13) % 6) / 20 - 0.12,
      );
      dummy.scale.set(scale, scale, scale);
      dummy.updateMatrix();
      leafyTrunks.setMatrixAt(index, dummy.matrix);
      dummy.position.y = surface + 0.72 * scale;
      dummy.scale.set(scale * 1.15, scale * 0.9, scale * 1.15);
      dummy.updateMatrix();
      leafyCanopies.setMatrixAt(index, dummy.matrix);
      leafyCanopies.setColorAt(
        index,
        variedColor(
          tile.continentId === "west" ? 0x8ad44a : 0x62d44a,
          tile.id,
          0.1,
        ),
      );
    });
    leafyTrunks.instanceMatrix.needsUpdate = true;
    leafyCanopies.instanceMatrix.needsUpdate = true;
    if (leafyCanopies.instanceColor)
      leafyCanopies.instanceColor.needsUpdate = true;
    leafyTrunks.castShadow = false;
    leafyCanopies.castShadow = false;
    treeGroup.add(leafyTrunks, leafyCanopies);
  }

  if (roughTiles.length > 0) {
    const rocks = new InstancedMesh(
      new DodecahedronGeometry(0.28, 0),
      new MeshLambertMaterial({
        color: 0xffffff,
        flatShading: true,
      }),
      roughTiles.length,
    );
    roughTiles.forEach((tile, index) => {
      const scale = 0.85 + ((tile.id * 7) % 8) / 14;
      dummy.position.set(
        tileWorldX(tile) + ((tile.id * 5) % 5) / 14 - 0.14,
        tileSurfaceY(tile) + 0.16 * scale,
        tileWorldZ(tile) + ((tile.id * 3) % 5) / 14 - 0.14,
      );
      dummy.scale.set(scale, scale * 0.7, scale);
      dummy.rotation.set(0.15, (tile.id % 8) * 0.45, 0.1);
      dummy.updateMatrix();
      rocks.setMatrixAt(index, dummy.matrix);
      rocks.setColorAt(index, variedColor(0xb9b3a4, tile.id, 0.08));
    });
    rocks.instanceMatrix.needsUpdate = true;
    if (rocks.instanceColor) rocks.instanceColor.needsUpdate = true;
    rocks.castShadow = false;
    scene.add(rocks);
  }

  const grassTiles = world.tiles.filter(
    (tile) =>
      tile.walkable &&
      !beach.has(tile.id) &&
      !isFreshwater(tile) &&
      tile.terrain === "grassland" &&
      tile.id % 11 === 0,
  );
  if (grassTiles.length > 0) {
    const tufts = new InstancedMesh(
      new ConeGeometry(0.11, 0.2, 5),
      new MeshLambertMaterial({
        color: 0xffffff,
        flatShading: true,
      }),
      grassTiles.length,
    );
    grassTiles.forEach((tile, index) => {
      const scale = 0.7 + ((tile.id * 5) % 6) / 16;
      dummy.position.set(
        tileWorldX(tile) + ((tile.id * 3) % 5) / 16 - 0.12,
        tileSurfaceY(tile) + 0.08 * scale,
        tileWorldZ(tile) + ((tile.id * 11) % 5) / 16 - 0.12,
      );
      dummy.scale.set(scale, scale, scale);
      dummy.rotation.set(0, (tile.id % 6) * 0.5, 0);
      dummy.updateMatrix();
      tufts.setMatrixAt(index, dummy.matrix);
      tufts.setColorAt(
        index,
        variedColor(
          tile.continentId === "west" ? 0x86c44a : 0x54c246,
          tile.id,
          0.1,
        ),
      );
    });
    tufts.instanceMatrix.needsUpdate = true;
    if (tufts.instanceColor) tufts.instanceColor.needsUpdate = true;
    tufts.castShadow = false;
    lodFarGroup.add(tufts);
  }
}

function resourceColor(family: ResourceFamily) {
  switch (family) {
    case "water":
      return 0x73d5d0;
    case "food":
      return 0xd5b650;
    case "wood":
      return 0x356644;
    case "stone":
      return 0xa8a69c;
  }
}

function createResourceVisual(family: ResourceFamily) {
  const group = new Group();

  if (family === "water") {
    const pool = new Mesh(
      new CircleGeometry(0.43, 18).rotateX(-Math.PI / 2),
      lambert(resourceColor(family), {
        emissive: 0x1b4e58,
        emissiveIntensity: 0.45,
      }),
    );
    const rim = new Mesh(
      new RingGeometry(0.42, 0.62, 18).rotateX(-Math.PI / 2),
      lambert(0xe8d39a),
    );
    pool.position.y = 0.035;
    rim.position.y = 0.02;
    group.add(rim, pool);
    return group;
  }

  if (family === "wood") {
    const trunk = configureMesh(
      new Mesh(new CylinderGeometry(0.12, 0.16, 0.72, 6), lambert(0x563a27)),
    );
    const canopy = configureMesh(
      new Mesh(new SphereGeometry(0.58, 8, 6), lambert(0x4fb83c)),
    );
    trunk.position.y = 0.36;
    canopy.position.y = 0.95;
    group.add(trunk, canopy);
    return group;
  }

  if (family === "food") {
    const soil = configureMesh(
      new Mesh(new BoxGeometry(0.9, 0.05, 0.9), lambert(0x6b4a28)),
    );
    soil.position.y = 0.03;
    const rowMaterial = lambert(0x5aaa38);
    for (let row = 0; row < 3; row += 1) {
      const crop = configureMesh(
        new Mesh(new BoxGeometry(0.72, 0.08, 0.12), rowMaterial),
      );
      crop.position.set(0, 0.08, (row - 1) * 0.22);
      group.add(crop);
    }
    group.add(soil);
    return group;
  }

  const stoneMaterial = lambert(resourceColor(family));
  const firstStone = configureMesh(
    new Mesh(new DodecahedronGeometry(0.4, 0), stoneMaterial),
  );
  const secondStone = configureMesh(
    new Mesh(new DodecahedronGeometry(0.27, 0), stoneMaterial),
  );
  firstStone.position.set(-0.12, 0.25, 0);
  secondStone.position.set(0.3, 0.17, 0.12);
  secondStone.rotation.y = 0.7;
  group.add(firstStone, secondStone);
  return group;
}

function createWildlifeVisual(kind: "predator-den" | "prey-range") {
  const group = new Group();
  const color = kind === "predator-den" ? 0x7e5147 : 0xb99b55;
  const markerMaterial = lambert(color);

  if (kind === "predator-den") {
    const den = configureMesh(
      new Mesh(new DodecahedronGeometry(0.5, 0), markerMaterial),
    );
    const opening = new Mesh(
      new CircleGeometry(0.22, 12),
      new MeshBasicMaterial({ color: 0x211b1b }),
    );
    den.scale.y = 0.7;
    den.position.y = 0.34;
    opening.position.set(0, 0.3, 0.42);
    group.add(den, opening);
    return group;
  }

  for (let index = 0; index < 3; index += 1) {
    const figure = configureMesh(
      new Mesh(new ConeGeometry(0.15, 0.42, 5), markerMaterial),
    );
    figure.position.set((index - 1) * 0.28, 0.21, (index % 2) * 0.2);
    group.add(figure);
  }
  return group;
}

function createHut(roofColor: number, wallColor: number, scale = 1) {
  const hut = new Group();
  const walls = configureMesh(
    new Mesh(
      new BoxGeometry(0.78 * scale, 0.48 * scale, 0.78 * scale),
      lambert(wallColor),
    ),
  );
  walls.position.y = 0.24 * scale;
  const roof = configureMesh(
    new Mesh(
      new ConeGeometry(0.68 * scale, 0.5 * scale, 4),
      lambert(roofColor),
    ),
  );
  roof.position.y = 0.58 * scale;
  roof.rotation.y = Math.PI / 4;
  const door = configureMesh(
    new Mesh(
      new BoxGeometry(0.18 * scale, 0.28 * scale, 0.04 * scale),
      lambert(0x4a3422),
    ),
  );
  door.position.set(0, 0.16 * scale, 0.4 * scale);
  hut.add(walls, roof, door);
  return hut;
}

function createCampYard(continentId: "east" | "west") {
  const group = new Group();
  const dirt = configureMesh(
    new Mesh(new BoxGeometry(3.2, 0.04, 3.2), lambert(0xc4a060)),
  );
  dirt.position.y = 0.01;
  group.add(dirt);
  const postMaterial = lambert(continentId === "west" ? 0x6a4428 : 0x7a5530);
  for (let index = 0; index < 16; index += 1) {
    const side = Math.floor(index / 4);
    const slot = (index % 4) / 3;
    const post = configureMesh(
      new Mesh(new BoxGeometry(0.08, 0.42, 0.08), postMaterial),
    );
    const edge = 1.55;
    if (side === 0) post.position.set(-edge + slot * edge * 2, 0.21, -edge);
    if (side === 1) post.position.set(edge, 0.21, -edge + slot * edge * 2);
    if (side === 2) post.position.set(edge - slot * edge * 2, 0.21, edge);
    if (side === 3) post.position.set(-edge, 0.21, edge - slot * edge * 2);
    group.add(post);
  }
  return group;
}

function buildingPalette(continentId: BuildingRecord["continentId"]) {
  return continentId === "west"
    ? { roof: 0xc45a32, wall: 0xb7a894 }
    : { roof: 0xd7b36a, wall: 0xc9c4b4 };
}

function emptyKits(): KitTemplates {
  return {
    hearth: null,
    hutEast: null,
    hutWest: null,
    leafy: null,
    pine: null,
    storage: null,
    work: null,
  };
}

function cloneKit(template: Group, scale = 1) {
  const model = template.clone(true);
  model.scale.multiplyScalar(scale);
  return model;
}

function collectMeshes(root: Object3D) {
  const meshes: Mesh<BufferGeometry, Material | Material[]>[] = [];
  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    meshes.push(object as Mesh<BufferGeometry, Material | Material[]>);
  });
  return meshes;
}

function instanceTemplate(
  template: Group,
  tiles: TileRecord[],
  place: (dummy: Object3D, tile: TileRecord, index: number) => void,
) {
  const group = new Group();
  if (tiles.length === 0) return group;
  template.updateMatrixWorld(true);
  const dummy = new Object3D();
  const composed = new Object3D();
  for (const mesh of collectMeshes(template)) {
    const instanced = new InstancedMesh(
      mesh.geometry,
      mesh.material,
      tiles.length,
    );
    instanced.castShadow = false;
    instanced.receiveShadow = false;
    instanced.frustumCulled = false;
    tiles.forEach((tile, index) => {
      place(dummy, tile, index);
      dummy.updateMatrix();
      composed.matrix.copy(dummy.matrix).multiply(mesh.matrixWorld);
      instanced.setMatrixAt(index, composed.matrix);
    });
    instanced.instanceMatrix.needsUpdate = true;
    group.add(instanced);
  }
  return group;
}

function createStationBody(
  kind: BuildingKind,
  continentId: "east" | "west",
  kits: KitTemplates,
) {
  const group = new Group();
  const palette = buildingPalette(continentId);
  const pad = configureMesh(
    new Mesh(new BoxGeometry(1.35, 0.05, 1.35), lambert(0xc4a060)),
  );
  pad.position.y = 0.02;
  group.add(pad);

  const hutKit = continentId === "west" ? kits.hutWest : kits.hutEast;
  if (kind === "shelter" && hutKit) {
    group.add(cloneKit(hutKit, 1.2));
    return group;
  }
  if (kind === "hearth" && kits.hearth) {
    group.add(cloneKit(kits.hearth, 1.15));
    return group;
  }
  if (kind === "storage" && kits.storage) {
    group.add(cloneKit(kits.storage, 1.2));
    return group;
  }
  if (kind === "work-surface" && kits.work) {
    group.add(cloneKit(kits.work, 1.15));
    return group;
  }

  if (kind === "shelter") {
    const hut = createHut(palette.roof, palette.wall, 1.55);
    hut.position.set(0, 0, 0.04);
    group.add(hut);
    return group;
  }

  if (kind === "hearth") {
    const ring = configureMesh(
      new Mesh(new TorusGeometry(0.36, 0.1, 6, 12), lambert(0x9a9588)),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 0.08;
    const logGeometry = new CylinderGeometry(0.05, 0.05, 0.5, 6).rotateZ(
      Math.PI / 2,
    );
    const logMaterial = lambert(0x5b3d24);
    const firstLog = configureMesh(new Mesh(logGeometry, logMaterial));
    const secondLog = configureMesh(new Mesh(logGeometry, logMaterial));
    firstLog.position.set(0, 0.08, 0);
    secondLog.position.copy(firstLog.position);
    secondLog.rotation.y = Math.PI / 2;
    const flame = new Mesh(
      new ConeGeometry(0.14, 0.38, 7),
      lambert(0xffb33f, { emissive: 0xff5a16, emissiveIntensity: 1.8 }),
    );
    flame.position.y = 0.28;
    group.add(ring, firstLog, secondLog, flame);
    return group;
  }

  if (kind === "storage") {
    const rack = createHut(0xb98a48, palette.wall, 1.15);
    const crate = configureMesh(
      new Mesh(new BoxGeometry(0.34, 0.22, 0.28), lambert(0x8a6232)),
    );
    crate.position.set(0.42, 0.14, 0.18);
    group.add(rack, crate);
    return group;
  }

  const slab = configureMesh(
    new Mesh(new BoxGeometry(0.95, 0.14, 0.7), lambert(0xb9b3a4)),
  );
  slab.position.y = 0.1;
  const flake = configureMesh(
    new Mesh(new DodecahedronGeometry(0.12, 0), lambert(0xc9c4b8)),
  );
  flake.position.set(0.22, 0.2, 0.08);
  group.add(slab, flake);
  return group;
}

function createBuildingVisual(
  building: BuildingRecord,
  kits: KitTemplates,
): BuildingVisual {
  const group = new Group();
  group.name = `building-${building.id}`;
  const body = createStationBody(building.kind, building.continentId, kits);
  const selectionRing = new Mesh(
    new RingGeometry(0.82, 1.02, 20).rotateX(-Math.PI / 2),
    new MeshBasicMaterial({
      color: 0xf5d57d,
      depthTest: false,
      opacity: 0.95,
      transparent: true,
    }),
  );
  selectionRing.position.y = 0.04;
  selectionRing.renderOrder = 3;
  selectionRing.visible = false;
  group.add(body, selectionRing);

  const pickTargets: Object3D[] = [];
  group.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    object.userData.buildingId = building.id;
    object.castShadow = false;
    object.receiveShadow = false;
    pickTargets.push(object);
  });

  return { group, pickTargets, selectionRing };
}

function addStaticWorldObjects(
  world: WorldState,
  scene: Scene,
  tileById: Map<number, TileRecord>,
  buildingVisuals: Map<string, BuildingVisual>,
  kits: KitTemplates,
) {
  for (const node of world.resourceNodes) {
    const tile = tileById.get(node.tileId);
    if (!tile) continue;
    const visual = createResourceVisual(node.family);
    visual.position.set(tileWorldX(tile), tileSurfaceY(tile), tileWorldZ(tile));
    visual.matrixAutoUpdate = false;
    visual.updateMatrix();
    scene.add(visual);
  }

  for (const site of world.wildlifeSites) {
    const tile = tileById.get(site.tileId);
    if (!tile) continue;
    const visual = createWildlifeVisual(site.kind);
    visual.position.set(tileWorldX(tile), tileSurfaceY(tile), tileWorldZ(tile));
    visual.matrixAutoUpdate = false;
    visual.updateMatrix();
    scene.add(visual);
  }

  for (const site of world.startSites) {
    const tile = tileById.get(site.tileId);
    if (!tile) continue;
    const yard = createCampYard(site.continentId);
    yard.position.set(tileWorldX(tile), tileSurfaceY(tile), tileWorldZ(tile));
    yard.matrixAutoUpdate = false;
    yard.updateMatrix();
    scene.add(yard);
  }

  for (const building of world.buildings) {
    const tile = tileById.get(building.tileId);
    if (!tile) continue;
    const visual = createBuildingVisual(building, kits);
    visual.group.position.set(
      tileWorldX(tile),
      tileSurfaceY(tile),
      tileWorldZ(tile),
    );
    buildingVisuals.set(building.id, visual);
    scene.add(visual.group);
  }
}

function actionColor(action: CreatureAction) {
  switch (action) {
    case "fleeing":
      return 0xd67a68;
    case "gathering":
      return 0xe8c77f;
    case "grazing":
      return 0x9bb56a;
    case "hunting":
      return 0xc45c3e;
    case "moving":
      return 0x8fc8c4;
    case "resting":
      return 0xa89cd0;
    default:
      return 0x9a9e91;
  }
}

function tintHumanoid(root: Object3D, clothColor: number, skinColor: number) {
  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    const materials = Array.isArray(object.material)
      ? object.material
      : [object.material];
    for (const material of materials) {
      const named = material as MeshLambertMaterial;
      const name = named.name ?? "";
      if (name.includes("Cloth")) named.color.setHex(clothColor);
      if (name.includes("Skin")) named.color.setHex(skinColor);
    }
  });
}

function convertMeshToLambert(root: Object3D) {
  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    object.castShadow = false;
    object.receiveShadow = false;
    const current = object.material as Material | Material[];
    const sources = Array.isArray(current) ? current : [current];
    const converted: Material[] = sources.map((material) => {
      if (!(material instanceof MeshStandardMaterial)) {
        return material;
      }
      const next = new MeshLambertMaterial({
        color: material.color,
        emissive: material.emissive,
        emissiveIntensity: material.emissiveIntensity,
        flatShading: true,
        map: material.map,
      });
      next.name = material.name;
      material.dispose();
      return next;
    });
    const first = converted[0];
    object.material = Array.isArray(current) ? converted : (first ?? current);
  });
}

function cloneHumanoid(template: Group, creature: CreatureRecord) {
  const model = template.clone(true);
  model.scale.setScalar(humanoidWorldScale);
  model.rotation.y = CAMERA_YAW;
  model.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    object.userData.creatureId = creature.id;
    object.castShadow = false;
    object.receiveShadow = false;
  });
  return model;
}

function tintedHumanoidTemplate(
  base: Group,
  clothColor: number,
  skinColor: number,
) {
  const template = base.clone(true);
  template.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    const mesh = object as Mesh<
      BufferGeometry,
      MeshLambertMaterial | MeshLambertMaterial[]
    >;
    if (Array.isArray(mesh.material)) {
      mesh.material = mesh.material.map((material) => material.clone());
    } else {
      mesh.material = mesh.material.clone();
    }
  });
  tintHumanoid(template, clothColor, skinColor);
  return template;
}

function createCreatureVisual(
  creature: CreatureRecord,
  humanoidTemplates: WorldRendererState["humanoidTemplates"],
): CreatureVisual {
  const group = new Group();
  const factionColor =
    creature.kind === "predator"
      ? 0x8a5a4c
      : creature.kind === "herbivore"
        ? 0xd0b05c
        : creature.continentId === "west"
          ? 0xc45a32
          : 0x5aa88a;
  const skinColor =
    creature.kind === "predator"
      ? 0x6a443c
      : creature.kind === "herbivore"
        ? 0xe4d09a
        : creature.continentId === "west"
          ? 0xe0a070
          : 0xd7c4a4;
  const pickTargets: Object3D[] = [];

  const humanoidTemplate = humanoidTemplates?.[creature.continentId];
  if (creature.kind === "humanoid" && humanoidTemplate) {
    const model = cloneHumanoid(humanoidTemplate, creature);
    model.traverse((object) => {
      if (object instanceof Mesh) pickTargets.push(object);
    });
    group.add(model);
  } else {
    const body = configureMesh(
      new Mesh(
        creature.kind === "predator"
          ? new DodecahedronGeometry(0.28, 0)
          : new SphereGeometry(0.18, 7, 5),
        lambert(factionColor),
      ),
    );
    const head = configureMesh(
      new Mesh(
        creature.kind === "predator"
          ? new ConeGeometry(0.16, 0.28, 5)
          : new ConeGeometry(0.12, 0.22, 5),
        lambert(skinColor),
      ),
    );
    body.position.y = creature.kind === "predator" ? 0.22 : 0.16;
    head.position.y = creature.kind === "predator" ? 0.42 : 0.32;
    body.userData.creatureId = creature.id;
    head.userData.creatureId = creature.id;
    pickTargets.push(body, head);
    group.add(body, head);
  }

  const selectionRing = new Mesh(
    new RingGeometry(0.16, 0.22, 16).rotateX(-Math.PI / 2),
    new MeshBasicMaterial({
      color: 0xf5d57d,
      depthTest: false,
      opacity: 0.95,
      transparent: true,
    }),
  );
  selectionRing.position.y = 0.035;
  selectionRing.renderOrder = 3;
  selectionRing.visible = false;

  const actionRing = new Mesh(
    new TorusGeometry(0.17, 0.018, 5, 12).rotateX(Math.PI / 2),
    new MeshBasicMaterial({
      color: actionColor(creature.action),
      opacity: 0.72,
      transparent: true,
    }),
  );
  actionRing.position.y = 0.09;
  actionRing.visible = creature.action !== "idle";

  group.add(selectionRing, actionRing);
  return {
    actionRing,
    group,
    pickTargets,
    selectionRing,
    targetPosition: new Vector3(),
  };
}

function updateDynamicWorld(
  renderState: WorldRendererState,
  world: WorldState,
  selectedCreatureId: string | null,
  selectedBuildingId: string | null,
) {
  const currentIds = new Set(world.creatures.map((creature) => creature.id));

  for (const [creatureId, visual] of renderState.creatureVisuals) {
    if (currentIds.has(creatureId)) continue;
    renderState.scene.remove(visual.group);
    disposeObject(visual.group);
    renderState.creatureVisuals.delete(creatureId);
  }

  for (const creature of world.creatures) {
    const tile = renderState.tileById.get(creature.tileId);
    if (!tile) continue;
    let visual = renderState.creatureVisuals.get(creature.id);
    if (!visual) {
      visual = createCreatureVisual(creature, renderState.humanoidTemplates);
      renderState.creatureVisuals.set(creature.id, visual);
      renderState.scene.add(visual.group);
      visual.group.position.set(
        tileWorldX(tile),
        tileSurfaceY(tile),
        tileWorldZ(tile),
      );
    }

    visual.targetPosition.set(
      tileWorldX(tile),
      tileSurfaceY(tile),
      tileWorldZ(tile),
    );
    visual.group.scale.setScalar(creature.alive ? 1 : 0.72);
    visual.selectionRing.visible = creature.id === selectedCreatureId;
    visual.actionRing.visible = creature.alive && creature.action !== "idle";
    const actionMaterial = visual.actionRing.material as MeshBasicMaterial;
    actionMaterial.color.setHex(actionColor(creature.action));
  }

  for (const [buildingId, visual] of renderState.buildingVisuals) {
    visual.selectionRing.visible = buildingId === selectedBuildingId;
  }

  renderState.creaturePickTargets = [
    ...renderState.creatureVisuals.values(),
  ].flatMap((visual) => visual.pickTargets);
  renderState.buildingPickTargets = [
    ...renderState.buildingVisuals.values(),
  ].flatMap((visual) => visual.pickTargets);

  const selectedCreature = world.creatures.find(
    (creature) => creature.id === selectedCreatureId,
  );
  const selectedTile = selectedCreature
    ? renderState.tileById.get(selectedCreature.tileId)
    : undefined;
  const targetTile =
    selectedCreature?.currentTargetTileId !== null &&
    selectedCreature?.currentTargetTileId !== undefined
      ? renderState.tileById.get(selectedCreature.currentTargetTileId)
      : undefined;

  if (selectedTile && targetTile) {
    renderState.routeLine.geometry.dispose();
    renderState.routeLine.geometry = new BufferGeometry().setFromPoints([
      new Vector3(
        tileWorldX(selectedTile),
        tileSurfaceY(selectedTile) + 0.18,
        tileWorldZ(selectedTile),
      ),
      new Vector3(
        tileWorldX(targetTile),
        tileSurfaceY(targetTile) + 0.18,
        tileWorldZ(targetTile),
      ),
    ]);
    renderState.routeLine.visible = true;
    renderState.destinationRing.position.set(
      tileWorldX(targetTile),
      tileSurfaceY(targetTile) + 0.08,
      tileWorldZ(targetTile),
    );
    renderState.destinationRing.visible = true;
  } else {
    renderState.routeLine.visible = false;
    renderState.destinationRing.visible = false;
  }
}

function isDisposableResource(value: unknown): value is DisposableResource {
  if (typeof value !== "object" || value === null) return false;
  return typeof (value as { dispose?: unknown }).dispose === "function";
}

function fitOrthographicToMap(
  camera: OrthographicCamera,
  pan: Vector2,
  aspect: number,
) {
  placeCamera(camera, pan);

  const point = new Vector3();
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const corner of worldFitCorners) {
    point.copy(corner).applyMatrix4(camera.matrixWorldInverse);
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }

  let viewWidth = (maxX - minX) * mapFitPadding;
  let viewHeight = (maxY - minY) * mapFitPadding;
  if (viewWidth / viewHeight > aspect) {
    viewHeight = viewWidth / aspect;
  } else {
    viewWidth = viewHeight * aspect;
  }

  camera.left = -viewWidth / 2;
  camera.right = viewWidth / 2;
  camera.top = viewHeight / 2;
  camera.bottom = -viewHeight / 2;
  camera.near = 0.5;
  camera.far = 420;
  camera.updateProjectionMatrix();
  return viewHeight;
}

function createPrecipitationMesh() {
  const mesh = new InstancedMesh(
    new BoxGeometry(0.05, 0.22, 0.05),
    new MeshBasicMaterial({
      color: 0xd8eef7,
      transparent: true,
      opacity: 0.55,
    }),
    24,
  );
  mesh.frustumCulled = false;
  mesh.visible = false;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  const dummy = new Object3D();
  for (let index = 0; index < 24; index += 1) {
    dummy.position.set(
      ((index * 17) % 96) - 48,
      4 + (index % 7) * 0.7,
      ((index * 13) % 64) - 32,
    );
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
}

function updateClimateVisuals(
  renderState: WorldRendererState,
  world: WorldState,
) {
  const { precipitation, season, temperature } = world.weather;
  const winter = season === "winter" || temperature < 0.28;
  renderState.scene.background = new Color(
    winter ? 0xb7c8d4 : precipitation === "rain" ? 0x6ea0b0 : 0x8ecfe0,
  );
  renderState.scene.fog = new FogExp2(
    winter ? 0xc5d3dc : 0xa8dce8,
    precipitation === "clear" ? 0.0012 : 0.0022,
  );
  const landMaterial = renderState.landMesh?.material;
  if (landMaterial && !Array.isArray(landMaterial)) {
    const terrain = landMaterial as MeshBasicMaterial;
    terrain.color.setHex(
      winter ? 0xc5d4c8 : season === "autumn" ? 0xf0d4a0 : 0xffffff,
    );
  }
  const flakes = renderState.precipitationMesh;
  if (flakes) {
    flakes.visible = precipitation !== "clear";
    const flakeMaterial = flakes.material as MeshBasicMaterial;
    flakeMaterial.color.setHex(precipitation === "snow" ? 0xf4fbff : 0x9ec7d8);
    flakeMaterial.opacity = precipitation === "snow" ? 0.85 : 0.4;
    flakes.scale.set(1, precipitation === "snow" ? 0.45 : 1, 1);
  }
}

function stepPrecipitation(mesh: InstancedMesh, snow: boolean) {
  const dummy = new Object3D();
  const speed = snow ? 0.08 : 0.22;
  for (let index = 0; index < mesh.count; index += 1) {
    mesh.getMatrixAt(index, dummy.matrix);
    dummy.matrix.decompose(dummy.position, dummy.quaternion, dummy.scale);
    dummy.position.y -= speed + (index % 5) * 0.01;
    if (dummy.position.y < 0.2) dummy.position.y = 8 + (index % 4);
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
}

function loadHumanoidTemplate(onLoad: (template: Group) => void) {
  loadKit("/models/kit-humanoid.glb", onLoad);
}

function loadKit(
  url: string,
  onLoad: (template: Group) => void,
  onError?: () => void,
) {
  const loader = new GLTFLoader();
  loader.load(
    url,
    (gltf) => {
      convertMeshToLambert(gltf.scene);
      gltf.scene.updateMatrixWorld(true);
      onLoad(gltf.scene);
    },
    undefined,
    () => onError?.(),
  );
}

function tintKitMaterial(root: Object3D, matcher: string, color: number) {
  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    const materials = Array.isArray(object.material)
      ? object.material
      : [object.material];
    for (const material of materials) {
      const named = material as MeshLambertMaterial;
      if ((named.name ?? "").includes(matcher)) named.color.setHex(color);
    }
  });
}

function factionHut(base: Group, roof: number, wall: number) {
  const template = base.clone(true);
  template.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    const mesh = object as Mesh<
      BufferGeometry,
      MeshLambertMaterial | MeshLambertMaterial[]
    >;
    if (Array.isArray(mesh.material)) {
      mesh.material = mesh.material.map((material) => material.clone());
    } else {
      mesh.material = mesh.material.clone();
    }
  });
  tintKitMaterial(template, "Thatch", roof);
  tintKitMaterial(template, "Plaster", wall);
  return template;
}

function clearGroup(group: Group) {
  while (group.children.length > 0) {
    const child = group.children[0];
    if (child) {
      group.remove(child);
      disposeObject(child);
    }
  }
}

function pineTilesOf(world: WorldState, beach: Set<number>) {
  return world.tiles.filter(
    (tile) =>
      tile.walkable &&
      !beach.has(tile.id) &&
      !isFreshwater(tile) &&
      tile.terrain === "forest" &&
      (tile.continentId === "west" || tile.id % 7 === 0) &&
      tile.id % 4 === 0,
  );
}

function leafyTilesOf(world: WorldState, beach: Set<number>) {
  return world.tiles.filter(
    (tile) =>
      tile.walkable &&
      !beach.has(tile.id) &&
      !isFreshwater(tile) &&
      (tile.terrain === "forest"
        ? tile.continentId === "east" && tile.id % 7 !== 0 && tile.id % 4 === 0
        : tile.terrain === "grassland" && tile.id % 22 === 0),
  );
}

function placeKitTrees(renderState: WorldRendererState) {
  const beach = collectShores(renderState.world).beach;
  const dummyPlace = (dummy: Object3D, tile: TileRecord, scaleBase: number) => {
    const scale = scaleBase + ((tile.id * 13) % 9) / 22;
    dummy.position.set(
      tileWorldX(tile) + ((tile.id * 11) % 7) / 22 - 0.12,
      tileSurfaceY(tile),
      tileWorldZ(tile) + ((tile.id * 17) % 7) / 22 - 0.12,
    );
    dummy.scale.setScalar(scale);
    dummy.rotation.set(0, (tile.id % 8) * 0.45, 0);
  };
  if (renderState.kits.pine) {
    const pines = pineTilesOf(renderState.world, beach);
    if (pines.length > 0) {
      renderState.decorationGroup.add(
        instanceTemplate(renderState.kits.pine, pines, (dummy, tile) =>
          dummyPlace(dummy, tile, 1.05),
        ),
      );
    }
  }
  if (renderState.kits.leafy) {
    const leafy = leafyTilesOf(renderState.world, beach);
    if (leafy.length > 0) {
      renderState.decorationGroup.add(
        instanceTemplate(renderState.kits.leafy, leafy, (dummy, tile) =>
          dummyPlace(dummy, tile, 1.0),
        ),
      );
    }
  }
}

function rebuildBuildings(renderState: WorldRendererState) {
  for (const visual of renderState.buildingVisuals.values()) {
    renderState.scene.remove(visual.group);
  }
  renderState.buildingVisuals.clear();
  for (const building of renderState.world.buildings) {
    const tile = renderState.tileById.get(building.tileId);
    if (!tile) continue;
    const visual = createBuildingVisual(building, renderState.kits);
    visual.group.position.set(
      tileWorldX(tile),
      tileSurfaceY(tile),
      tileWorldZ(tile),
    );
    renderState.buildingVisuals.set(building.id, visual);
    renderState.scene.add(visual.group);
  }
  renderState.buildingPickTargets = [
    ...renderState.buildingVisuals.values(),
  ].flatMap((visual) => visual.pickTargets);
}

function rebuildHumanoids(
  renderState: WorldRendererState,
  world: WorldState,
  selectedCreatureId: string | null,
  selectedBuildingId: string | null,
) {
  for (const [creatureId, visual] of [...renderState.creatureVisuals]) {
    const creature = world.creatures.find((entry) => entry.id === creatureId);
    if (!creature || creature.kind !== "humanoid") continue;
    renderState.scene.remove(visual.group);
    disposeObject(visual.group);
    renderState.creatureVisuals.delete(creatureId);
  }
  updateDynamicWorld(
    renderState,
    world,
    selectedCreatureId,
    selectedBuildingId,
  );
}

function disposeObject(root: Object3D) {
  const resources = new Set<DisposableResource>();

  root.traverse((object) => {
    const disposable = object as Object3D & {
      geometry?: unknown;
      material?: unknown;
    };
    if (isDisposableResource(disposable.geometry)) {
      resources.add(disposable.geometry);
    }
    if (isDisposableResource(disposable.material)) {
      resources.add(disposable.material);
    } else if (Array.isArray(disposable.material)) {
      for (const material of disposable.material as unknown[]) {
        if (isDisposableResource(material)) resources.add(material);
      }
    }
  });

  resources.forEach((resource) => resource.dispose());
}

function applyLod(renderState: WorldRendererState, zoom: number) {
  renderState.lodFarGroup.visible = zoom >= 4.5;
}

export function WorldCanvas({
  onSelectBuilding,
  onSelectCreature,
  selectedBuildingId,
  selectedCreatureId,
  world,
}: WorldCanvasProperties) {
  const containerReference = useRef<HTMLDivElement>(null);
  const rendererStateReference = useRef<WorldRendererState | null>(null);
  const worldReference = useRef(world);
  const selectedCreatureReference = useRef(selectedCreatureId);
  const selectedBuildingReference = useRef(selectedBuildingId);
  const onSelectCreatureReference = useRef(onSelectCreature);
  const onSelectBuildingReference = useRef(onSelectBuilding);
  const [zoom, setZoom] = useState(1);
  const [gpuEpoch, setGpuEpoch] = useState(0);
  const zoomReference = useRef(zoom);
  const worldSeed = world?.seed;

  useEffect(() => {
    worldReference.current = world;
    selectedCreatureReference.current = selectedCreatureId;
    selectedBuildingReference.current = selectedBuildingId;
    onSelectCreatureReference.current = onSelectCreature;
    onSelectBuildingReference.current = onSelectBuilding;
    zoomReference.current = zoom;
  }, [
    onSelectBuilding,
    onSelectCreature,
    selectedBuildingId,
    selectedCreatureId,
    world,
    zoom,
  ]);

  useEffect(() => {
    const host = containerReference.current;
    const currentWorld = worldReference.current;
    if (!host || !currentWorld) return;

    const scene = new Scene();
    scene.background = new Color(0x74c8e6);
    scene.fog = new FogExp2(0x9ed8ea, 0.0009);

    const renderer = new WebGLRenderer({
      antialias: true,
      powerPreference: "high-performance",
    });
    renderer.outputColorSpace = SRGBColorSpace;
    renderer.shadowMap.enabled = false;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.25));
    renderer.domElement.setAttribute(
      "aria-label",
      "Three-dimensional world map",
    );
    host.appendChild(renderer.domElement);

    const camera = new OrthographicCamera(-1, 1, 1, -1, 0.5, 420);
    const pan = new Vector2(0, 0);
    const tileById = new Map(currentWorld.tiles.map((tile) => [tile.id, tile]));
    const creatureVisuals = new Map<string, CreatureVisual>();
    const routeLine = new Line(
      new BufferGeometry(),
      new LineBasicMaterial({
        color: 0xf5d57d,
        opacity: 0.8,
        transparent: true,
      }),
    );
    routeLine.visible = false;
    routeLine.renderOrder = 4;
    scene.add(routeLine);
    const destinationRing = new Mesh(
      new RingGeometry(0.34, 0.48, 24).rotateX(-Math.PI / 2),
      new MeshBasicMaterial({
        color: 0xf5d57d,
        depthTest: false,
        opacity: 0.9,
        transparent: true,
      }),
    );
    destinationRing.visible = false;
    destinationRing.renderOrder = 4;
    scene.add(destinationRing);

    const hemisphereLight = new HemisphereLight(0xe8f7ff, 0x6a9a48, 1.35);
    const sun = new DirectionalLight(0xfff1c8, 1.55);
    sun.position.set(48, 72, 58);
    scene.add(hemisphereLight, sun);

    const terrainRoot = new Group();
    terrainRoot.name = "terrain-root";
    scene.add(terrainRoot);
    const shores = createTerrainMeshes(currentWorld, terrainRoot);
    const lodFarGroup = new Group();
    lodFarGroup.name = "lod-far";
    scene.add(lodFarGroup);
    const precipitationMesh = createPrecipitationMesh();
    scene.add(precipitationMesh);
    const decorationGroup = new Group();
    decorationGroup.name = "kit-trees";
    scene.add(decorationGroup);
    createTerrainDecorations(
      currentWorld,
      scene,
      shores.beach,
      lodFarGroup,
      decorationGroup,
    );
    const kits = emptyKits();
    const buildingVisuals = new Map<string, BuildingVisual>();
    addStaticWorldObjects(currentWorld, scene, tileById, buildingVisuals, kits);

    let baseViewHeight = WORLD_HEIGHT;
    const updateCamera = () => {
      placeCamera(camera, pan);
      camera.updateProjectionMatrix();
    };
    const resize = () => {
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      const aspect = width / height;
      const savedZoom = camera.zoom;
      camera.zoom = 1;
      baseViewHeight = fitOrthographicToMap(camera, new Vector2(0, 0), aspect);
      camera.zoom = savedZoom;
      updateCamera();
      renderer.setSize(width, height, false);
    };

    const renderState: WorldRendererState = {
      buildingPickTargets: [],
      buildingVisuals,
      camera,
      creaturePickTargets: [],
      creatureVisuals,
      decorationGroup,
      destinationRing,
      humanoidTemplates: null,
      kits,
      landMesh: shores.land,
      lodFarGroup,
      onSelectBuilding: onSelectBuildingReference.current,
      onSelectCreature: onSelectCreatureReference.current,
      pan,
      pointerTravel: 0,
      precipitationMesh,
      renderer,
      resetView: () => {
        pan.set(0, 0);
        updateCamera();
      },
      routeLine,
      scene,
      setZoom: (nextZoom) => {
        camera.zoom = nextZoom;
        renderer.setPixelRatio(
          nextZoom >= 2.4 ? Math.min(window.devicePixelRatio || 1, 1.5) : 1.25,
        );
        updateCamera();
        applyLod(renderState, nextZoom);
      },
      tileById,
      world: currentWorld,
    };
    rendererStateReference.current = renderState;
    renderState.setZoom(zoomReference.current);
    updateDynamicWorld(
      renderState,
      worldReference.current ?? currentWorld,
      selectedCreatureReference.current,
      selectedBuildingReference.current,
    );
    updateClimateVisuals(renderState, worldReference.current ?? currentWorld);
    loadHumanoidTemplate((template) => {
      if (rendererStateReference.current !== renderState) return;
      renderState.humanoidTemplates = {
        east: tintedHumanoidTemplate(template, 0x5aa88a, 0xd7c4a4),
        west: tintedHumanoidTemplate(template, 0xc45a32, 0xe0a070),
      };
      rebuildHumanoids(
        renderState,
        worldReference.current ?? currentWorld,
        selectedCreatureReference.current,
        selectedBuildingReference.current,
      );
    });
    const kitPalette = buildingPalette("west");
    const eastPalette = buildingPalette("east");
    loadKit("/models/kit-hut.glb", (template) => {
      if (rendererStateReference.current !== renderState) return;
      renderState.kits.hutWest = factionHut(
        template,
        kitPalette.roof,
        kitPalette.wall,
      );
      renderState.kits.hutEast = factionHut(
        template,
        eastPalette.roof,
        eastPalette.wall,
      );
      rebuildBuildings(renderState);
    });
    loadKit("/models/kit-hearth.glb", (template) => {
      if (rendererStateReference.current !== renderState) return;
      renderState.kits.hearth = template;
      rebuildBuildings(renderState);
    });
    loadKit("/models/kit-storage.glb", (template) => {
      if (rendererStateReference.current !== renderState) return;
      renderState.kits.storage = template;
      rebuildBuildings(renderState);
    });
    loadKit("/models/kit-work.glb", (template) => {
      if (rendererStateReference.current !== renderState) return;
      renderState.kits.work = template;
      rebuildBuildings(renderState);
    });
    loadKit("/models/kit-pine.glb", (template) => {
      if (rendererStateReference.current !== renderState) return;
      renderState.kits.pine = template;
      if (!renderState.kits.leafy) return;
      clearGroup(renderState.decorationGroup);
      placeKitTrees(renderState);
    });
    loadKit("/models/kit-leafy.glb", (template) => {
      if (rendererStateReference.current !== renderState) return;
      renderState.kits.leafy = template;
      if (!renderState.kits.pine) return;
      clearGroup(renderState.decorationGroup);
      placeKitTrees(renderState);
    });

    const raycaster = new Raycaster();
    const pointer = new Vector2();
    let dragging = false;
    let previousX = 0;
    let previousY = 0;
    const canvas = renderer.domElement;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      setZoom((current) =>
        clamp(
          current * (event.deltaY < 0 ? 1.16 : 0.86),
          minimumZoom,
          maximumZoom,
        ),
      );
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      dragging = true;
      renderState.pointerTravel = 0;
      previousX = event.clientX;
      previousY = event.clientY;
      canvas.setPointerCapture?.(event.pointerId);
      canvas.style.cursor = "grabbing";
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (!dragging) return;
      const deltaX = event.clientX - previousX;
      const deltaY = event.clientY - previousY;
      renderState.pointerTravel += Math.hypot(deltaX, deltaY);
      const unitsPerPixel = baseViewHeight / host.clientHeight / camera.zoom;
      const axes = cameraPanAxes();
      pan.x = clamp(
        pan.x - (deltaX * axes.rightX + deltaY * axes.downX) * unitsPerPixel,
        -WORLD_WIDTH * 0.42,
        WORLD_WIDTH * 0.42,
      );
      pan.y = clamp(
        pan.y - (deltaX * axes.rightZ + deltaY * axes.downZ) * unitsPerPixel,
        -WORLD_HEIGHT * 0.42,
        WORLD_HEIGHT * 0.42,
      );
      previousX = event.clientX;
      previousY = event.clientY;
      updateCamera();
    };
    const selectAtPointer = (event: PointerEvent) => {
      const bounds = canvas.getBoundingClientRect();
      pointer.set(
        ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
        -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
      );
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(
        [
          ...renderState.buildingPickTargets,
          ...renderState.creaturePickTargets,
        ],
        false,
      )[0];
      const hitData = hit?.object.userData as
        Record<string, unknown> | undefined;
      const creatureId = hitData?.creatureId;
      const buildingId = hitData?.buildingId;
      if (typeof creatureId === "string") {
        renderState.onSelectCreature(creatureId);
        return;
      }
      if (typeof buildingId === "string") {
        renderState.onSelectBuilding(buildingId);
      }
    };
    const handlePointerUp = (event: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      canvas.releasePointerCapture?.(event.pointerId);
      canvas.style.cursor = "grab";
      if (renderState.pointerTravel < 6) selectAtPointer(event);
    };
    const handlePointerCancel = (event: PointerEvent) => {
      dragging = false;
      canvas.releasePointerCapture?.(event.pointerId);
      canvas.style.cursor = "grab";
    };

    canvas.style.cursor = "grab";
    canvas.addEventListener("wheel", handleWheel, { passive: false });
    canvas.addEventListener("pointerdown", handlePointerDown);
    canvas.addEventListener("pointermove", handlePointerMove);
    canvas.addEventListener("pointerup", handlePointerUp);
    canvas.addEventListener("pointercancel", handlePointerCancel);

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    let lastFrameTime = 0;
    renderer.setAnimationLoop((time) => {
      if (document.hidden) return;
      if (time - lastFrameTime < frameIntervalMs) return;
      lastFrameTime = time;
      for (const visual of creatureVisuals.values()) {
        visual.group.position.lerp(visual.targetPosition, 0.16);
      }
      const flakes = renderState.precipitationMesh;
      const weather = worldReference.current?.weather;
      if (flakes?.visible && weather && weather.precipitation !== "clear") {
        stepPrecipitation(flakes, weather.precipitation === "snow");
      }
      renderer.render(scene, camera);
    });

    const handleContextLost = (event: Event) => {
      event.preventDefault();
      renderer.setAnimationLoop(null);
    };
    const handleContextRestored = () => {
      setGpuEpoch((epoch) => epoch + 1);
    };
    canvas.addEventListener("webglcontextlost", handleContextLost);
    canvas.addEventListener("webglcontextrestored", handleContextRestored);

    return () => {
      renderer.setAnimationLoop(null);
      resizeObserver.disconnect();
      canvas.removeEventListener("wheel", handleWheel);
      canvas.removeEventListener("pointerdown", handlePointerDown);
      canvas.removeEventListener("pointermove", handlePointerMove);
      canvas.removeEventListener("pointerup", handlePointerUp);
      canvas.removeEventListener("pointercancel", handlePointerCancel);
      canvas.removeEventListener("webglcontextlost", handleContextLost);
      canvas.removeEventListener("webglcontextrestored", handleContextRestored);
      if (rendererStateReference.current === renderState) {
        rendererStateReference.current = null;
      }
      disposeObject(scene);
      renderer.dispose();
      canvas.remove();
    };
  }, [gpuEpoch, worldSeed]);

  useEffect(() => {
    const renderState = rendererStateReference.current;
    if (!renderState || !world) return;
    renderState.onSelectCreature = onSelectCreatureReference.current;
    renderState.onSelectBuilding = onSelectBuildingReference.current;
    updateDynamicWorld(
      renderState,
      world,
      selectedCreatureId,
      selectedBuildingId,
    );
    updateClimateVisuals(renderState, world);
  }, [
    onSelectBuilding,
    onSelectCreature,
    selectedBuildingId,
    selectedCreatureId,
    world,
  ]);

  useEffect(() => {
    rendererStateReference.current?.setZoom(zoom);
  }, [zoom]);

  return (
    <div className="world-canvas-frame world-canvas-frame-3d">
      <div className="world-canvas" ref={containerReference} />
      <div className="map-tools" aria-label="Map controls">
        <button
          type="button"
          onClick={() =>
            setZoom((current) =>
              clamp(current - 0.45, minimumZoom, maximumZoom),
            )
          }
          aria-label="Zoom out"
        >
          −
        </button>
        <span>{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          onClick={() =>
            setZoom((current) =>
              clamp(current + 0.45, minimumZoom, maximumZoom),
            )
          }
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          className="map-reset"
          type="button"
          onClick={() => {
            setZoom(1);
            rendererStateReference.current?.resetView();
          }}
          aria-label="Reset map view"
        >
          ⌂
        </button>
      </div>
      <div className="map-legend" aria-label="Map legend">
        <span>
          <i className="map-swatch map-swatch-west" /> West / aggressive
        </span>
        <span>
          <i className="map-swatch map-swatch-east" /> East / peaceful
        </span>
        <span>
          <i className="map-swatch map-swatch-resource" /> Resource
        </span>
        <span>
          <i className="map-swatch map-swatch-station" /> Camp station
        </span>
      </div>
      <div className="map-compass" aria-hidden="true">
        <span>N</span>
        <b>✦</b>
      </div>
    </div>
  );
}
